export type AbortReason = 'timeout' | 'external';

export interface LinkedSignal {
  signal: AbortSignal;
  /** Which side aborted, or null if neither did. */
  getReason: () => AbortReason | null;
  dispose: () => void;
}

/**
 * Combine a caller-supplied signal with a timeout into one signal, while
 * remembering *which* one fired so a timeout can be reported as
 * `GpmPayTimeoutError` rather than a bare `AbortError`.
 *
 * Hand-rolled rather than using `AbortSignal.any`, which needs Node >= 20.3.
 */
export function linkSignals(
  external: AbortSignal | undefined,
  timeoutMs: number,
): LinkedSignal {
  const controller = new AbortController();
  let reason: AbortReason | null = null;

  const onExternal = () => {
    if (reason === null) reason = 'external';
    controller.abort();
  };

  if (external?.aborted === true) {
    onExternal();
  } else if (external) {
    external.addEventListener('abort', onExternal, { once: true });
  }

  const timer = setTimeout(() => {
    if (reason === null) reason = 'timeout';
    controller.abort();
  }, timeoutMs);
  // Don't hold the event loop open just for a pending request timeout.
  (timer as unknown as { unref?: () => void }).unref?.();

  return {
    signal: controller.signal,
    getReason: () => reason,
    dispose: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternal);
    },
  };
}

export class AbortError extends Error {
  override readonly name = 'AbortError';
  constructor(message = 'The operation was aborted.') {
    super(message);
  }
}

/** `setTimeout` as a promise that rejects with `AbortError` if the signal fires. */
export function abortableSleep(
  ms: number,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new AbortError());
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    function onAbort() {
      clearTimeout(timer);
      reject(new AbortError());
    }

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
